import { assertNever } from "@@/shared/assert-never";
import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type { Transaction } from "@@/shared/settlements/transactions";
import type { Except } from "type-fest";
import { z } from "zod";
import type {
    FeeSourceTransactionsId} from "./fee-source-transactions-id";
import {
    feeSourceTransactionsIdFactory,
    feeSourceTransactionsIdZodSchema,
} from "./fee-source-transactions-id";
import type {
    FeeSourceTransactionsFilterId} from "./filters/fee-source-transactions-filter-id";
import {
    feeSourceTransactionsFilterIdZodSchema,
} from "./filters/fee-source-transactions-filter-id";
import type {
    FeeSourceTransactionsFilter} from "./filters/fee-source-transactions-filters";
import {
    feeSourceTransactionsFilterZodSchema,
} from "./filters/fee-source-transactions-filters";

/**
 * Fee source for managing fees on order data \
 * E.g. fees for orders with a special delivery type, or orders bought on a specific destination etc.
 */
type FeeSourceTransactions = {
    readonly _id: FeeSourceTransactionsId;
    readonly _type: "FEE_SOURCE";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly type: "TRANSACTIONS";
    // readonly conditions: Record<
    //     FeeSourceTransactionConditionId,
    //     FeeSourceTransactionCondition
    // >;
    // readonly conditionTransaction: FeeSourceTransactionConditionId[];
    readonly filters: Record<
        FeeSourceTransactionsFilterId,
        FeeSourceTransactionsFilter
    >;
    readonly filterTransactions: FeeSourceTransactionsFilterId[];
};

type Params = Except<
    FeeSourceTransactions,
    "_id" | "type" | "_type" | "_createdAt"
>;
const feeSourceTransactionFactory = (params: Params): FeeSourceTransactions => {
    return {
        ...params,
        _id: feeSourceTransactionsIdFactory(),
        _type: "FEE_SOURCE",
        _createdAt: nowIsoAndUnix(),
        type: "TRANSACTIONS",
    };
};

const feeSourceTransactionZodSchema: z.ZodSchema<FeeSourceTransactions> =
    z.object({
        _id: feeSourceTransactionsIdZodSchema,
        _type: z.literal("FEE_SOURCE"),
        _createdAt: isoAndUnixZodSchema,
        type: z.literal("TRANSACTIONS"),

        filters: z.record(
            feeSourceTransactionsFilterIdZodSchema,
            feeSourceTransactionsFilterZodSchema,
        ) as z.ZodSchema<FeeSourceTransactions["filters"]>, // quick fix zod issue,
        filterTransactions: z.array(feeSourceTransactionsFilterIdZodSchema),
    });

/**
 * Returns all orders of given order available to the fee source parameters
 * @param {Transaction[]} transactions
 * @param {FeeSourceTransactions} feeSource
 * @return {*}  {Order[]}
 */
const feeSourceTransactions = (
    transactions: Transaction[],
    feeSource: FeeSourceTransactions,
): Transaction[] => {
    const filters = feeSource.filterTransactions.map(
        filterId => feeSource.filters[filterId],
    );

    const filtered = filters.reduce(
        (result, filter) => {
            const filtered = result.filter(transaction => {
                const filterType = filter.type;
                const invert = "invert" in filter ? filter.invert : false;
                let filterMatch = false;

                switch (filterType) {
                    case "PAYMENT_PROVIDER":
                        filterMatch =
                            transaction.paymentProvider ===
                            filter.paymentProvider;
                        break;
                    case "TRANSACTION_TYPE":
                        filterMatch =
                            transaction.transactionType ===
                            filter.transactionType;
                        break;
                    default:
                        assertNever(filterType);
                }
                return invert ? !filterMatch : filterMatch;
            });
            return filtered;
        },
        [...transactions],
    );

    return filtered;
};

export {
    feeSourceTransactionFactory,
    feeSourceTransactions,
    feeSourceTransactionZodSchema,
};
export type { FeeSourceTransactions };
